/* global Office */

/**
 * `Office.context.document.settings` への低レベルラッパー。
 *
 * 設計方針:
 * - 値の取得 / 設定 (L1: メモリ) は同期で完結する
 * - L2 (`saveAsync` で document へ反映) は**シリアライズキュー方式**で直列化する
 * - debounce は行わない。`writeAndQueueSave` の各呼び出しでメモリ即時更新 + L2 保存スケジュール
 * - L2 保存が in-flight の間に新しい変更が来た場合、現在の保存完了後に再度 1 回だけ saveAsync を発火
 *
 * これにより:
 * - ユーザー体感の遅延はゼロ (L1 即時、UI 再レンダーは shapePromptStore 側で即時通知)
 * - saveAsync の race condition (順序逆転) を排除
 * - 最後の状態が必ず最終的に document に反映される
 *
 * ## キューの完了を待てる口を持つ
 * 書き込みは投げっぱなしで良いが、**pptx の実体を取り出す前だけは待たなければならない**。生成開始は
 * `getFileAsync` で現在の文書を取り出してアップロードし、サーバーはその pptx から `im.prompts` を
 * 読む。L2 へ反映される前に取り出すと、直前の編集が入っていない pptx で生成が走る。
 * :func:`flushSettings` がその待ちと、失敗の申告を担う。
 *
 * ## 失敗は状態として外へ出す
 * 待つ側へ投げるだけでは、設定を書いている最中の失敗が誰にも届かない。読み取り専用で開いた
 * pptx や共有の競合では日常的に起きるので、**書いた設定がファイルへ残らないまま作業が進む**。
 * そこで同じ出来事を購読できる状態（{@link SaveFailure}）としても持ち、画面が帯で出し続ける。
 * 設計: [[2026-09-15 設計ノート アドイン 設定の保存失敗の申告]]。
 */

let pending = false;
/** 走っている flush ループ。無ければ null。:func:`flushSettings` の待ち先。 */
let inflight: Promise<void> | null = null;
/**
 * 直近の saveAsync の失敗。成功で消える。
 *
 * 書き込み側は投げっぱなしなので握ってログに出すだけだが、**待つ側には伝える**。握ったままだと、
 * 保存できていない設定のまま生成が始まり、画面で見ている設定と実際に使われる設定が食い違う。
 */
let lastError: Error | null = null;

/**
 * いま保存できていないか。できていれば `null`。
 *
 * `lastError` と同じ出来事を、**画面が購読できる形**で持つ。`lastError` は `flushSettings` が
 * 一度きり申告するための値で、投げた時点で読み手が居なければ消えてしまう。
 */
export type SaveFailure = { message: string } | null;

let failure: SaveFailure = null;
const failureListeners = new Set<() => void>();

/**
 * 失敗の状態を差し替えて購読者へ知らせる。**同じ状態なら何もしない。**
 *
 * 保存は打鍵のたびに走るので、成功のたびに通知すると画面が無駄に描き直される。
 * `useSyncExternalStore` が参照で同一性を見るため、同じ内容の器を作り直さないことも要る。
 */
function setFailure(next: SaveFailure): void {
    if (failure === null && next === null) return;
    if (failure && next && failure.message === next.message) return;
    failure = next;
    failureListeners.forEach((l) => l());
}

/** いま保存できていないか。できていれば `null`。 */
export function getSaveFailure(): SaveFailure {
    return failure;
}

/** 保存の成否が変わったときに呼ばれる。戻り値で購読を解除する。 */
export function subscribeSaveFailure(cb: () => void): () => void {
    failureListeners.add(cb);
    return () => {
        failureListeners.delete(cb);
    };
}

/**
 * 保存をやり直す。通れば `true`。
 *
 * **`flushSettings` では代用できない。** 失敗したあと何も書いていないと `pending` が立って
 * いないため、あちらは保存を試さずに古い失敗を投げ直す。読み取り専用を解除した・共有の競合が
 * 解けた、といった場面で利用者が自分で確かめられるよう、必ず 1 回走らせる口を分けて持つ。
 */
export async function retrySave(): Promise<boolean> {
    pending = true;
    while (inflight || pending) {
        await (inflight ?? flush());
    }
    return failure === null;
}

/**
 * settings に値を書き込み、L2 への直列キュー flush をスケジュールする。
 *
 * 失敗時の挙動: saveAsync が失敗した場合、ログに記録するのみ。
 * 次回の writeAndQueueSave 呼び出しで再試行される。
 */
export function writeAndQueueSave<T>(key: string, value: T): void {
    Office.context.document.settings.set(key, value);
    pending = true;
    void flush();
}

/**
 * L2 への保存が出尽くすまで待つ。**直近の保存が失敗していれば投げる。**
 *
 * pptx の実体を取り出す前に呼ぶ (生成開始)。待っている間に積まれた書き込みも待ち切る。
 * 保存する物が無ければ即座に返る。
 */
export async function flushSettings(): Promise<void> {
    while (inflight || pending) {
        await (inflight ?? flush());
    }
    if (lastError) throw lastError;
}

/**
 * settings からキーを消し、L2 への直列キュー flush をスケジュールする。
 *
 * **移行し終えた旧キーを残さないために使う。** `im.shapePrompts`（暗黙 v1）は読み替えたあとも
 * ファイルの中に残り続けていた。消す口が無かったからで、そのため 1 つの pptx に同じ設定の
 * 古い写しと新しい正本が並び、外部用のスクラブや別環境での取り込みで拾われる余地が残っていた
 * （記録 10 の申し送り）。
 *
 * 消す順は「新しい鍵を書いたあと」である。保存は settings 全体を 1 度で書くので、先に消しても
 * 同じ結果になるが、保存が失敗したときに古い写しだけが失われる形を避ける。
 */
export function removeAndQueueSave(key: string): void {
    Office.context.document.settings.remove(key);
    pending = true;
    void flush();
}

/**
 * settings からキーの現在値を取得する。値が無い場合は undefined。
 */
export function readSetting<T>(key: string): T | undefined {
    const v = Office.context.document.settings.get(key);
    return v === undefined || v === null ? undefined : (v as T);
}

function flush(): Promise<void> {
    if (inflight) return inflight;
    inflight = (async () => {
        while (pending) {
            pending = false;
            try {
                await saveAsync();
                // 保存は settings 全体を書くので、通れば前の失敗ぶんも載っている。
                lastError = null;
                setFailure(null);
            } catch (e) {
                // 失敗しても pending は次回の writeAndQueueSave で再設定される。
                // ここで再 throw すると flush ループ自体が壊れるため握りつぶし、
                // 代わりに lastError へ残して flushSettings が申告する。
                lastError = e instanceof Error ? e : new Error(String(e));
                setFailure({ message: lastError.message });
                // eslint-disable-next-line no-console
                console.error("settings.saveAsync failed", e);
            }
        }
    })().finally(() => {
        inflight = null;
    });
    return inflight;
}

function saveAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
        Office.context.document.settings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve();
            } else {
                reject(new Error(result.error?.message ?? "saveAsync failed"));
            }
        });
    });
}

/**
 * テスト用: 内部キュー状態を初期化する。本番コードでは呼ばない。
 */
export function __resetForTest(): void {
    pending = false;
    inflight = null;
    lastError = null;
    failure = null;
    failureListeners.clear();
}
